import {
  formatDateTime,
  formatMoney,
  formatNumber,
  formatPercent,
  fromCents,
  type Resource,
  routes,
  type Scenario,
  sheetHasData,
  summarizeSheet,
  type SummaryColumn,
} from '@pmai/contracts';
import { Alert, Button, ConfirmDialog, DataTable, useToast } from '@pmai/ui';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from '../../../shared/api/hooks';
import { usePermissions } from '../../../shared/auth/session';
import { CardSection } from '../../../shared/card-section';
import { useErrorText } from '../../../shared/i18n';
import {
  addMonth,
  addRow,
  copyPlanToFact,
  type EditRow,
  type EditSheet,
  removeLastMonth,
  removeLastMonthCheck,
  removeRow,
} from './sheet-edit';
import { TimesheetGrid } from './timesheet-grid';
import type { SaveState, useTimesheetEditor } from './use-timesheet-editor';

type Editor = ReturnType<typeof useTimesheetEditor>;

/** "💰 Finansiālā pārvaldība nav iespējota" (FR-PRJ-023). */
export function FinanceOff() {
  const { t } = useTranslation();
  return (
    <p className="rounded-lg bg-canvas px-4 py-3 text-sm text-ink-muted">{t('timesheets.financeOff')}</p>
  );
}

function SaveStatus({ state }: { state: SaveState }) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  if (state.state === 'idle') return null;
  if (state.state === 'failed')
    return (
      <span role="alert" className="text-xs font-medium text-red-600" title={errorText(state.error)}>
        {t('timesheets.status.failed')}
      </span>
    );
  return (
    <span role="status" className="text-xs text-ink-muted">
      {t(`timesheets.status.${state.state}`)}
    </span>
  );
}

type Pending = { kind: 'month' } | { kind: 'row'; row: EditRow } | { kind: 'copy' } | null;

/** "📋 Plāns" / "✅ Fakts" with their actions (FR-TS-001…016). */
export function SheetSection({
  scenario,
  editor,
  financialManagement,
}: {
  scenario: Scenario;
  editor: Editor;
  financialManagement: boolean;
}) {
  const { t } = useTranslation();
  const notify = useToast();
  const permissions = usePermissions();
  const finance = permissions.includes('finance.view');
  const editable = finance && permissions.includes('timesheets.edit');
  const resources = useApiQuery(routes.resources.list, {}, { enabled: editable });
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const sheet = editor.sheets[scenario];
  const hidden = scenario === 'plan' && !financialManagement;
  const change = (fn: (s: EditSheet) => EditSheet) => {
    setNotice(null);
    editor.change(scenario, fn);
  };

  const askRemoveMonth = () => {
    const check = removeLastMonthCheck(sheet);
    if (check === 'only') setNotice(t('timesheets.oneMonth'));
    else if (check === 'locked') setNotice(t('timesheets.lockedMonth'));
    else if (check === 'data') setPending({ kind: 'month' });
    else change(removeLastMonth);
  };
  const askRemoveRow = (row: EditRow) =>
    sheet.rows.length <= 1 ? setNotice(t('timesheets.oneResource')) : setPending({ kind: 'row', row });
  const askCopy = () => {
    const { plan, fact } = editor.sheets;
    if (!sheetHasData(plan)) setNotice(t('timesheets.planEmpty'));
    else if (sheetHasData(fact)) setPending({ kind: 'copy' });
    else copy();
  };
  const copy = () => {
    editor.change('fact', (fact) => copyPlanToFact(editor.sheets.plan, fact));
    notify(t('timesheets.copied'));
  };

  const confirm: Record<
    NonNullable<Pending>['kind'],
    { title: string; text: string; word: string; run: () => void }
  > = {
    month: {
      title: t('timesheets.removeMonthTitle'),
      text: t('timesheets.removeMonthText'),
      word: 'delete',
      run: () => change(removeLastMonth),
    },
    row: {
      title: t('timesheets.removeResourceTitle'),
      text: t('timesheets.removeResourceText', { name: pending?.kind === 'row' ? pending.row.name : '' }),
      word: 'DELETE',
      run: () => pending?.kind === 'row' && change((s) => removeRow(s, pending.row.key)),
    },
    copy: {
      title: t('timesheets.copyTitle'),
      text: t('timesheets.copyText'),
      word: 'overwrite',
      run: copy,
    },
  };
  const dialog = pending ? confirm[pending.kind] : null;

  return (
    <CardSection
      id={`project.${scenario}`}
      title={t(`timesheets.${scenario}`)}
      actions={
        !hidden && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <SaveStatus state={editor.saveState[scenario]} />
            {editable && (
              <>
                <Button size="sm" variant="secondary" icon="calendar-plus" onClick={() => change(addMonth)}>
                  {t('timesheets.addMonth')}
                </Button>
                <Button size="sm" variant="secondary" icon="calendar-minus" onClick={askRemoveMonth}>
                  {t('timesheets.removeMonth')}
                </Button>
                <Button size="sm" variant="secondary" icon="user-plus" onClick={() => change(addRow)}>
                  {t('timesheets.addResource')}
                </Button>
                {scenario === 'plan' && (
                  <Button size="sm" variant="secondary" icon="copy" onClick={askCopy}>
                    {t('timesheets.copyToFact')}
                  </Button>
                )}
              </>
            )}
          </div>
        )
      }
    >
      {hidden ? (
        <FinanceOff />
      ) : (
        <div className="flex flex-col gap-3">
          {notice && <Alert tone="danger">{notice}</Alert>}
          <TimesheetGrid
            scenario={scenario}
            sheet={sheet}
            editable={editable}
            finance={finance}
            resources={resources.data ?? ([] as Resource[])}
            onChange={change}
            onRemoveRow={askRemoveRow}
          />
        </div>
      )}
      <ConfirmDialog
        open={dialog !== null}
        onOpenChange={(open) => !open && setPending(null)}
        title={dialog?.title ?? ''}
        message={dialog?.text ?? ''}
        confirmWord={dialog?.word}
        confirmLabel={t('common.confirm')}
        onConfirm={() => {
          dialog?.run();
          setPending(null);
        }}
      />
    </CardSection>
  );
}

const SUMMARY_ROWS = [
  'hours',
  'expenses',
  'invoicedAdvance',
  'invoiced',
  'revenue',
  'balance',
  'toInvoice',
  'invoicedPct',
  'profit',
  'margin',
] as const satisfies readonly (keyof SummaryColumn)[];
type SummaryRow = (typeof SUMMARY_ROWS)[number];

function summaryValue(row: SummaryRow, c: SummaryColumn): ReactNode {
  if (row === 'hours') return formatNumber(c.hours);
  if (row === 'invoicedPct' || row === 'margin') return formatPercent(c[row]);
  const cents = c[row];
  const text = formatMoney(fromCents(cents));
  if (row !== 'balance' && row !== 'profit') return text;
  return (
    <span className={cents < 0 ? 'text-red-600' : cents > 0 ? 'text-emerald-700' : undefined}>{text}</span>
  );
}

function SummaryTable({ scenario, sheet }: { scenario: Scenario; sheet: EditSheet }) {
  const { t } = useTranslation();
  const columns = summarizeSheet(sheet);
  const title = t(`enum.scenario.${scenario}`);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="text-xs text-ink-muted" data-testid={`updated-${scenario}`}>
          {sheet.updatedAt ? t('timesheets.updated', { at: formatDateTime(sheet.updatedAt) }) : '–'}
        </span>
      </div>
      <DataTable
        label={`${t('timesheets.summary')} — ${title}`}
        rows={SUMMARY_ROWS.map((row) => ({ row }))}
        rowKey={(r) => r.row}
        empty={null}
        columns={[
          { key: 'label', header: '', cell: (r) => t(`timesheets.summaryRows.${r.row}`) },
          ...columns.map((c) => ({
            key: c.year === null ? 'total' : String(c.year),
            header: c.year === null ? t('timesheets.total') : String(c.year),
            align: 'right' as const,
            className: c.year === null ? 'font-semibold' : undefined,
            cell: (r: { row: SummaryRow }) => summaryValue(r.row, c),
          })),
        ]}
      />
    </div>
  );
}

/** "📊 Finanšu kopsavilkums" — Plan and Fact by year (FR-TS-018/019); finance data only. */
export function FinancialSummarySection({
  editor,
  financialManagement,
}: {
  editor: Editor;
  financialManagement: boolean;
}) {
  const { t } = useTranslation();
  return (
    <CardSection id="project.summary" title={t('timesheets.summary')}>
      <div className="grid gap-6 xl:grid-cols-2">
        {financialManagement ? <SummaryTable scenario="plan" sheet={editor.sheets.plan} /> : <FinanceOff />}
        <SummaryTable scenario="fact" sheet={editor.sheets.fact} />
      </div>
    </CardSection>
  );
}
